import { getBuiltInDatabaseLayoutTypes } from '@databases/types/database/view';
import { err, ok } from 'neverthrow';

import { isReservedLayoutTypeId } from './types.ts';
import type {
  InferPropertiesMap,
  TypedViewLayoutRegistry,
  ViewLayoutDataModule,
  ViewLayoutModuleContext,
  ViewLayoutModuleMode,
  ViewLayoutRegistryError,
  ViewLayoutRegistry
} from './types.ts';

export type CreateViewLayoutRegistryOptions<
  Modules extends readonly ViewLayoutDataModule<string, Record<string, unknown>>[] =
    ViewLayoutDataModule[]
> = {
  modules?: Modules;
  context?: ViewLayoutModuleContext;
  builtInLayoutIds?: string[];
};

const notFound = (layoutId: string): ViewLayoutRegistryError => ({
  layoutId,
  reason: 'VIEW_LAYOUT_NOT_FOUND',
  message: `View layout "${layoutId}" is not registered`
});

const writeDisabled = (
  layoutId: string,
  mode: ViewLayoutModuleMode
): ViewLayoutRegistryError => ({
  layoutId,
  reason: 'VIEW_LAYOUT_WRITE_DISABLED',
  message: `View layout "${layoutId}" is ${mode} and cannot be written`
});

/**
 * Creates a registry for one database/app context.
 *
 * Built-in layouts are always writable. Extension layouts are writable only
 * when a registered module returns `read-write`; `read-only` preserves durable
 * data while blocking writes, and `off` behaves as unsupported.
 *
 * When called with a `const` module array, the return type carries a
 * layout-to-property type map. The SDK uses this map for typed overloads
 * on layout property methods.
 */
export function createViewLayoutRegistry<
  const Modules extends readonly ViewLayoutDataModule<string, Record<string, unknown>>[]
>(
  options: CreateViewLayoutRegistryOptions<Modules> = {}
): TypedViewLayoutRegistry<InferPropertiesMap<Modules>> & ViewLayoutRegistry {
  const {
    modules,
    context = {},
    builtInLayoutIds = getBuiltInDatabaseLayoutTypes()
  } = options;
  const moduleList: readonly ViewLayoutDataModule<string, Record<string, unknown>>[] =
    modules ?? [];

  for (const module of moduleList) {
    if (isReservedLayoutTypeId(module.id)) {
      throw new Error(
        `createViewLayoutRegistry: layout id "${module.id}" is a reserved ` +
          `built-in storage code and cannot be registered as a layout-module id.`
      );
    }
  }

  const modulesById = new Map(moduleList.map((module) => [module.id, module]));
  const builtIns = new Set(builtInLayoutIds);

  const registry: ViewLayoutRegistry &
    TypedViewLayoutRegistry<InferPropertiesMap<Modules>> = {
    getMode(layoutId) {
      // A registered module ALWAYS drives its own mode. This is the "flip a
      // switch" seam: a layout goes from gated/experimental to core purely by
      // changing its module's getMode (gated → always `read-write`), and a
      // layout can be feature-gated even if it later joins the built-in set.
      // The built-in set is only a fallback so the core layouts stay writable
      // when no module is registered for them (e.g. the default registry).
      const module = modulesById.get(layoutId);
      if (module) {
        return module.getMode(context);
      }

      return builtIns.has(layoutId) ? 'read-write' : 'off';
    },

    getModule(layoutId) {
      return modulesById.get(layoutId);
    },

    isKnown(layoutId) {
      return registry.getMode(layoutId) !== 'off';
    },

    isWritable(layoutId) {
      return registry.getMode(layoutId) === 'read-write';
    },

    listWritableLayouts() {
      const writable = new Set<string>();

      // Built-ins with no registered module stay writable by default.
      for (const id of builtIns) {
        if (!modulesById.has(id)) {
          writable.add(id);
        }
      }

      // Module-backed layouts (including built-in ids) defer to their mode.
      for (const module of moduleList) {
        if (module.getMode(context) === 'read-write') {
          writable.add(module.id);
        }
      }

      return [...writable];
    },

    parseProperties(layoutId, properties) {
      const module = modulesById.get(layoutId);

      if (!module) {
        return builtIns.has(layoutId) ? ok(undefined) : err(notFound(layoutId));
      }

      if (module.getMode(context) === 'off') {
        return err(notFound(layoutId));
      }

      return (
        module.parseProperties?.(properties) ?? ok(properties as Record<string, unknown>)
      );
    },

    requireWritable(layoutId) {
      const mode = registry.getMode(layoutId);

      if (mode === 'off') {
        return err(notFound(layoutId));
      }

      if (mode !== 'read-write') {
        return err(writeDisabled(layoutId, mode));
      }

      return ok(undefined);
    }
  };

  return registry;
}

/**
 * Built-in-only registry used by existing callers.
 *
 * This is deliberately default-deny for extension layouts: persisted extension
 * data can still round-trip through model/collab conversion, but writes require
 * an app-provided registry that explicitly registers the module.
 */
export const defaultViewLayoutRegistry = createViewLayoutRegistry();
